"use client";

import { Info } from "lucide-react";
import { Switch } from "@/components/shadcn/switch";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/shadcn/hover-card";
import { SpotMarkdown } from "@/components/Spotflow/SpotMarkdown/SpotMarkdown";
import { cn } from "@/lib/utils";

export interface MCPToolRowTool {
  name: string;
  description?: string | null;
}

export function humanizeToolName(slug: string): string {
  return slug
    .split(/[_-]+/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export function MCPToolRow({
  tool,
  integrationImage,
  integrationName,
  checked,
  onToggle,
  disabled,
}: {
  tool: MCPToolRowTool;
  integrationImage?: string;
  integrationName?: string;
  checked: boolean;
  onToggle: () => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        "flex items-start gap-3 rounded-md border border-border/60 px-3 py-2 hover:bg-accent/40",
        disabled && "opacity-60 pointer-events-none"
      )}
    >
      {integrationImage && (
        <img
          src={integrationImage}
          alt={integrationName || ""}
          className="size-5 rounded-sm object-contain mt-0.5 shrink-0"
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <span className="truncate">{humanizeToolName(tool.name)}</span>
          {tool.description && (
            <HoverCard openDelay={150} closeDelay={100}>
              <HoverCardTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground shrink-0"
                  aria-label={`Description for ${tool.name}`}
                  onClick={(e) => e.preventDefault()}
                >
                  <Info className="size-3.5" />
                </button>
              </HoverCardTrigger>
              <HoverCardContent
                side="top"
                align="start"
                className="w-112 max-h-80 p-0 overflow-hidden flex flex-col"
              >
                <div className="px-4 pt-3 pb-2 text-xs font-mono text-muted-foreground border-b shrink-0">
                  {tool.name}
                </div>
                <div className="px-4 py-3 text-xs overflow-y-auto overscroll-contain min-h-0">
                  <SpotMarkdown markdown={tool.description} />
                </div>
              </HoverCardContent>
            </HoverCard>
          )}
        </div>
        {tool.description && (
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {tool.description}
          </div>
        )}
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onToggle}
        disabled={disabled}
        className="mt-0.5 shrink-0"
      />
    </label>
  );
}
